import { supabase } from '@/integrations/supabase/client';
import { UserRoleInfo, UserRole } from '@/types/auth';
import { toast } from 'sonner';

/**
 * Checks the user's role from the database or sets a default role
 */
export async function checkUserRole(userId: string, email: string): Promise<UserRole> {
  console.log('Checking role for user:', userId, email);
  
  try {
    // First check if user exists in profiles table
    const { data: profile, error: profileError } = await supabase
      .from('profiles')
      .select('user_type')
      .eq('user_id', userId)
      .single();

    if (profileError) {
      console.error('Error fetching profile:', profileError);
      // If no profile exists, create one based on email
      let userType: UserRole = 'investor'; // Default role
      
      // Admin emails list - ONLY the official admin email
      const adminEmails = [
        'amrish@h44s.co.za'
      ];
      
      // Check for admin emails - ONLY for new profiles
      if (adminEmails.includes(email.toLowerCase())) {
        userType = 'admin';
      } else if (email.endsWith('@entrepreneur.com')) {
        userType = 'entrepreneur';
      }

      // Create new profile
      const { error: insertError } = await supabase
        .from('profiles')
        .insert({
          id: userId, // Keep id for foreign key reference
          user_id: userId, // Add user_id for querying
          email: email,
          user_type: userType,
          onboarding_completed: false
        });

      if (insertError) {
        console.error('Error creating profile:', insertError);
        // Try to update if insert fails (in case profile exists but select failed)
        const { error: updateError } = await supabase
          .from('profiles')
          .update({ 
            email: email,
            onboarding_completed: false
          })
          .eq('user_id', userId);
          
        if (updateError) {
          console.error('Error updating profile:', updateError);
          return userType; // Return determined role even if save fails
        }
      }

      return userType;
    }

    // For existing profiles, just return their current role
    return profile.user_type as UserRole;
  } catch (error) {
    console.error('Error in checkUserRole:', error);
    return 'investor'; // Default to investor role if something goes wrong
  }
}

/**
 * Updates user data in investor_metrics, creating a record if it doesn't exist
 */
export const ensureUserMetrics = async (userId: string): Promise<void> => {
  try {
    // Check if user already has metrics
    const { data, error } = await supabase
      .from('investor_metrics')
      .select('*')
      .eq('user_id', userId)
      .maybeSingle();
    
    if (error && error.code !== 'PGRST116') {
      console.error('Error checking investor metrics:', error);
      return;
    }
    
    // If no metrics found, create default ones
    if (!data) {
      const { error: insertError } = await supabase
        .from('investor_metrics')
        .insert({
          user_id: userId,
          total_investments: 0,
          portfolio_value: 0,
          avg_return_rate: 0,
          dividends_received: 0,
          viewed_profiles: 0,
          active_deals: 0
        });
      
      if (insertError) {
        console.error('Error creating default metrics:', insertError);
      }
    }
  } catch (error) {
    console.error('Error in ensureUserMetrics:', error);
  }
};
